/**
 * Where "Build for devices" shows, for a project's mobile services only (GET /device-builds › apps; nothing for any
 * other service, nor while the answer is missing or failed):
 * - `DeviceBuildButton`: the button in a service's header on Apps;
 * - `DeviceBuildsCard`: the service's builds on its Overview in Apps (with the same button);
 * - `PublishDeviceBuilds`: one line in the Publish dialog, a button per mobile service.
 * Each opens DeviceBuildDialog; the builds come from useDeviceBuilds (shared, read every 5 s while one runs).
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'antd';
import { DeviceMobile } from '@phosphor-icons/react';
import useDeviceBuilds from '../../hooks/useDeviceBuilds';
import { mobileAppOf } from '../../services/DeviceBuildsService';
import { sameService } from '../../utils/serviceTabsModel';
import { Card } from '../apps/ServiceTabs/common';
import DeviceBuildDialog from './DeviceBuildDialog';
import DeviceBuildsList from './DeviceBuildsList';
import './DeviceBuilds.css';

function BuildButton({ projectId, app, available, size = 'middle', testId = 'devb-open', label = 'Build for devices' }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button data-testid={testId} icon={<DeviceMobile aria-hidden size={14} />} onClick={() => setOpen(true)} size={size} title="Build an app to install on Android phones or iPhones, or for the stores">
        {label}
      </Button>
      <DeviceBuildDialog app={app} available={available} onClose={() => setOpen(false)} open={open} projectId={projectId} />
    </>
  );
}
BuildButton.propTypes = {
  app: PropTypes.object.isRequired, available: PropTypes.object, label: PropTypes.string, projectId: PropTypes.string.isRequired, size: PropTypes.string, testId: PropTypes.string,
};

export function DeviceBuildButton({ projectId, service, readOnly = false }) {
  const { data } = useDeviceBuilds(projectId);
  const app = mobileAppOf(data, service);
  if (readOnly || !app) { return null; }
  return <BuildButton app={app} available={data.available} projectId={projectId} />;
}
DeviceBuildButton.propTypes = { projectId: PropTypes.string, readOnly: PropTypes.bool, service: PropTypes.string };

export function DeviceBuildsCard({ projectId, service, readOnly = false }) {
  const { data, refresh } = useDeviceBuilds(projectId);
  const app = mobileAppOf(data, service);
  if (!app) { return null; }
  const builds = (data.builds || []).filter((build) => sameService(build.service, service));
  return (
    <Card testId="devb-card" title="Builds for devices" wide>
      <p className="devb-muted">
        {app.label || 'Mobile'} app: build it for Android phones and iPhones, to test it or to send it to the stores.
        {data.available?.enabled === false && data.available.reason ? ` ${data.available.reason}` : ''}
      </p>
      <DeviceBuildsList builds={builds} onChanged={refresh} projectId={projectId} readOnly={readOnly} />
      {!readOnly && (
        <div className="devb-row">
          <BuildButton app={app} available={data.available} label={builds.length ? 'New build' : 'Build for devices'} projectId={projectId} size="small" testId="devb-card-open" />
        </div>
      )}
    </Card>
  );
}
DeviceBuildsCard.propTypes = { projectId: PropTypes.string, readOnly: PropTypes.bool, service: PropTypes.string };

export function PublishDeviceBuilds({ projectId }) {
  const { data } = useDeviceBuilds(projectId);
  const apps = data?.apps || [];
  if (apps.length === 0) { return null; }
  return (
    <div className="devb-publish" data-testid="devb-publish">
      <span>Mobile {apps.length === 1 ? 'app' : 'apps'}: build for Android phones and iPhones, or for the stores.</span>
      {apps.map((app) => (
        <BuildButton
          app={app}
          available={data.available}
          key={app.service}
          label={apps.length === 1 ? 'Build for devices' : `Build ${app.service}`}
          projectId={projectId}
          size="small"
          testId={`devb-publish-${app.service}`}
        />
      ))}
    </div>
  );
}
PublishDeviceBuilds.propTypes = { projectId: PropTypes.string };
